<template>
  <button
    type="button"
    class="wizard-action-btn"
    :class="`wizard-action-btn--${variant}`"
    :disabled="disabled || loading"
    @click="$emit('click')"
  >
    <template v-if="!loading">
      <i
        v-if="icon"
        :class="icon"
        class="wizard-action-btn__icon"
      />
      <span class="wizard-action-btn__label">{{ label }}</span>
      <i
        v-if="trailingIcon"
        :class="trailingIcon"
        class="wizard-action-btn__trailing"
      />
    </template>
    <template v-else>
      <i class="fa-regular fa-spinner-third fa-spin wizard-action-btn__icon" />
      <span class="wizard-action-btn__label">{{ loadingLabel }}</span>
    </template>
  </button>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

type Variant = 'next' | 'pay';

export default defineComponent({
  name: 'WizardActionButton',

  props: {
    label: {
      type: String,
      required: true,
    },
    /** Leading icon class (e.g. `fa-regular fa-lock`). */
    icon: {
      type: String,
      default: '',
    },
    /** Trailing icon class (e.g. `fa-regular fa-arrow-right` for next CTAs). */
    trailingIcon: {
      type: String,
      default: '',
    },
    /** `next` = compact pill (Continuer →) ; `pay` = bigger CTA with lock. */
    variant: {
      type: String as PropType<Variant>,
      default: 'next',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingLabel: {
      type: String,
      default: '',
    },
  },

  emits: ['click'],
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.wizard-action-btn {
  appearance: none;
  border: none;
  background: $brand-color-primary;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 600;
  border-radius: 100px;
  cursor: pointer;
  transition: all 160ms ease;
  box-shadow: 0 4px 12px -4px rgba(148, 200, 64, 0.5);
  line-height: 1;

  &--next {
    padding: 10px 18px 10px 20px;
    font-size: 0.9375rem;

    &:hover:not(:disabled) {
      background: #7aae26;
      transform: translateX(2px);
      box-shadow: 0 8px 20px -4px rgba(148, 200, 64, 0.65);
    }

    .wizard-action-btn__trailing {
      transition: transform 160ms ease;
    }

    &:hover:not(:disabled) .wizard-action-btn__trailing {
      transform: translateX(2px);
    }
  }

  &--pay {
    padding: 12px 20px;
    font-size: 0.9375rem;

    &:hover:not(:disabled) {
      background: #7aae26;
      transform: translateY(-1px);
      box-shadow: 0 8px 20px -4px rgba(148, 200, 64, 0.65);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }

  &__icon {
    font-size: 0.875rem;
  }

  &__trailing {
    font-size: 0.8125rem;
  }
}
</style>
